import type { Move } from '../../../schemas/move';

/**
 * Archetipi del Warlock (manuale V1, rr. 5836-5840).
 *
 * 5 mosse di tipo `class_special`. Pattern razza-tematico standard.
 *
 * **Cross-archetipo description-only**:
 * - **Elfo**: «aggiungi un trucchetto dalla lista del Mago alle suppliche [...] e quando
 *   prendi *Le Vie del Patrono...*, puoi scegliere anche tra le mosse del Mago» — la
 *   regola estende il pool della mossa multiclasse *Le Vie del Patrono...* (L2-5),
 *   description-only (FI-17).
 * - **Mezz'orco**: «l'arma che ti viene concessa con il *Vincolo della Lama* ha di base il
 *   miglioramento *truce* [...] e quando prendi *Le Vie del Patrono...*, puoi scegliere
 *   anche tra le mosse del Guerriero» — analogo cross-class description-only.
 *
 * **Nano e immunità ai veleni (D5)**: archetipo Nano del Warlock concede gratuitamente
 * l'opzione "*immunità ai veleni*" del *Contratto* (manuale r. 5885: «*gratis* per
 * l'archetipo del Warlock: «Nano»»). Effetto narrativo runtime, description-only.
 * Niente `extraTags` schema (rinviato indefinitamente, coerente con FI-1 caso secondario
 * documentato come description-only).
 */

const archetipoUmano: Move = {
  id: 'warlock.archetipo-umano',
  name: 'Archetipo: Umano',
  type: 'class_special',
  classId: 'warlock',
  trigger:
    'Quando usi un credito ottenuto con *Vincolo della Catena* come leva per *parlamentare* con un PNG',
  description:
    'Puoi trovare una scusa per non considerare saldato il suo debito. Tira+**CAR**. Con 10+, il PNG si considera ancora in debito con te. Con 7-9, ottieni comunque un 10+ in *parlamentare*, ma il credito che avevi è perso. Con 6-, lo stesso, ma non puoi segnare il PE per il fallimento.',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Il PNG si considera ancora in debito con te.' },
    on7to9: { text: 'Ottieni 10+ in *parlamentare*, ma il credito è perso.' },
    on6minus: { text: 'Come 7-9, ma non puoi segnare il PE per il fallimento.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoElfo: Move = {
  id: 'warlock.archetipo-elfo',
  name: 'Archetipo: Elfo',
  type: 'class_special',
  classId: 'warlock',
  trigger:
    'Quando hai il *Vincolo del Tomo* (le suppliche includono un trucchetto del Mago) o quando prendi *Le Vie del Patrono...*',
  description:
    'Aggiungi un trucchetto dalla lista del Mago alle suppliche che ti sono concesse con il *Vincolo del Tomo*, e quando **prendi *Le Vie del Patrono...***, puoi scegliere anche tra le mosse del Mago.\n\n**NB**: estensione cross-archetipo del pool della mossa multiclasse *Le Vie del Patrono...* (L2-5). Description-only, FI-17 traccia il pattern multiclasse strutturato per il futuro.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

// L'opzione «immunità ai veleni» è una delle 12 di beneficio del *Contratto*
// (manuale r. 5885); per l'archetipo Nano è concessa gratis. Effetto narrativo
// runtime, description-only.
const archetipoNano: Move = {
  id: 'warlock.archetipo-nano',
  name: 'Archetipo: Nano',
  type: 'class_special',
  classId: 'warlock',
  trigger: 'Sempre (effetto continuo sul *Contratto*)',
  description:
    "Ottieni «*immunità ai veleni*» oltre ai benefici che ti sono concessi dal tuo *Contratto*.\n\nL'opzione «immunità ai veleni» è in aggiunta ai 2 benefici che scegli normalmente.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoHalfling: Move = {
  id: 'warlock.archetipo-halfling',
  name: 'Archetipo: Halfling',
  type: 'class_special',
  classId: 'warlock',
  trigger: 'Quando usi *La Fortuna degli Sciocchi*',
  description: 'Prendi +1 a qualsiasi prossimo tiro invece che solo su *sfidare il pericolo*.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

// FI-17: estensione cross-archetipo del pool di *Le Vie del Patrono*.
// L'effetto «scegli anche tra le mosse del Guerriero» è description-only
// finché il pool multiclasse non riconoscerà l'archetipo Mezz'orco runtime.
const archetipoMezzorco: Move = {
  id: 'warlock.archetipo-mezzorco',
  name: "Archetipo: Mezz'orco",
  type: 'class_special',
  classId: 'warlock',
  trigger:
    "Quando hai il *Vincolo della Lama* (l'arma ha *truce* di base) o quando prendi *Le Vie del Patrono...*",
  description:
    "L'arma che ti viene concessa con il *Vincolo della Lama* ha di base il miglioramento «*truce*» oltre a quelli che scegli, e quando **prendi *Le Vie del Patrono...***, puoi scegliere anche tra le mosse del Guerriero.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const warlockArchetypeMoves: readonly Move[] = [
  archetipoUmano,
  archetipoElfo,
  archetipoNano,
  archetipoHalfling,
  archetipoMezzorco,
] as const;
